import { toCitySlug } from "@/lib/citySlug";

export const formatSlug = (slug) => {
  if (!slug) return "";
  // Drop trailing opaque id suffixes that include a digit
  // (e.g. event-name-00xzcx → Event Name). Keep real words like "new-york".
  const cleaned = decodeURIComponent(slug).replace(
    /-(?=[a-z0-9]*\d)[a-z0-9]{4,12}$/i,
    "",
  );
  return cleaned
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Maps system route keys to friendly readable terms
 */
export const routeLabelMap = {
  explore: "Explore",
  category: "Categories",
  categories: "Categories",
  events: "Events",
  event: "Events",
  contests: "Contests",
  contest: "Contest",
  book: "Book Tickets",
  blogs: "Blogs",
  venues: "Venues",
  organizer: "Organizers",
  blog: "Blog",
  artist: "Artists",
  artists: "Artists",
};

export const getCleanCitySlug = (city) => toCitySlug(city);

export const generateBreadcrumbsFromPath = (
  pathname,
  customLabels = {},
  currentPageTitle,
  currentCity = "ahmedabad",
) => {
  if (!pathname || pathname === "/") {
    return [];
  }

  const paths = pathname.split("/").filter(Boolean);
  const city = getCleanCitySlug(currentCity);

  // 1. City + Category + Event detail: Home > City > Category > Events > title (e.g. /ahmedabad/music/events/slug)
  if (
    paths.length >= 4 &&
    ["event", "events"].includes(paths[2].toLowerCase())
  ) {
    const routeCity = paths[0];
    const category = paths[1];
    const eventSegment = paths[2].toLowerCase();
    const eventSlug = paths[3];
    const isBook = paths[4]?.toLowerCase() === "book";

    const items = [
      {
        title: "Home",
        href: "/",
      },
      {
        title: formatSlug(routeCity),
        href: `/${routeCity}/events`,
      },
      {
        title: customLabels[category] || formatSlug(category),
        href: `/${routeCity}/${category}`,
      },
      {
        title: isBook
          ? formatSlug(eventSlug)
          : currentPageTitle || formatSlug(eventSlug),
        href: isBook
          ? `/${routeCity}/${category}/${eventSegment}/${eventSlug}`
          : undefined,
      },
    ];

    if (isBook) {
      items.push({
        title: "Book Tickets",
      });
    }

    return items;
  }

  // 2. City + Event detail: Home > City > Events > title (e.g. /ahmedabad/event/slug or /ahmedabad/events/slug)
  if (
    paths.length >= 3 &&
    ["event", "events"].includes(paths[1].toLowerCase())
  ) {
    const routeCity = paths[0];
    const eventSegment = paths[1].toLowerCase();
    const eventSlug = paths[2];
    const isBook = paths[3]?.toLowerCase() === "book";
    const eventLabel = routeLabelMap[eventSegment] || "Events";

    const items = [
      {
        title: "Home",
        href: "/",
      },
      // DEV-023: one crumb per URL. The city and "Events" crumbs both pointed
      // at /{city}/events, which Google reports as a duplicate list item.
      {
        title: `${eventLabel} in ${formatSlug(routeCity)}`,
        href: `/${routeCity}/events`,
      },
      {
        title: currentPageTitle || formatSlug(eventSlug),
        href: isBook ? `/${routeCity}/${eventSegment}/${eventSlug}` : undefined,
      },
    ];

    if (isBook) {
      items.push({
        title: "Book Tickets",
      });
    }

    return items;
  }

  // 3. Event route without city prefix (e.g. /event/slug, /events/slug, /events/slug/book)
  if (
    paths.length >= 2 &&
    ["event", "events"].includes(paths[0].toLowerCase())
  ) {
    const eventSegment = paths[0].toLowerCase();
    const eventSlug = paths[1];
    const isBook = paths[2]?.toLowerCase() === "book";
    const eventLabel = routeLabelMap[eventSegment] || "Events";

    const items = [
      {
        title: "Home",
        href: "/",
      },
      {
        title: eventLabel,
        href: `/${city}/events`,
      },
      {
        title: currentPageTitle || formatSlug(eventSlug),
        href: isBook ? `/${eventSegment}/${eventSlug}` : undefined,
      },
    ];

    if (isBook) {
      items.push({
        title: "Book Tickets",
      });
    }

    return items;
  }

  // 4. Contest detail with city prefix: Home > City > Contest > title (e.g. /ahmedabad/contest/slug or /ahmedabad/contests/slug)
  if (
    paths.length >= 3 &&
    ["contest", "contests"].includes(paths[1].toLowerCase())
  ) {
    const routeCity = paths[0];
    const contestSegment = paths[1].toLowerCase();
    const contestSlug = paths[2];
    const contestLabel = routeLabelMap[contestSegment] || "Contest";

    return [
      {
        title: "Home",
        href: "/",
      },
      {
        title: formatSlug(routeCity),
        href: `/${routeCity}/contests`,
      },
      {
        title: contestLabel,
        href: `/${routeCity}/contests`,
      },
      {
        title: currentPageTitle || formatSlug(contestSlug),
      },
    ];
  }

  // 5. Contest route without city prefix (e.g. /contest/slug, /contests/slug)
  if (
    paths.length >= 2 &&
    ["contest", "contests"].includes(paths[0].toLowerCase())
  ) {
    const contestSegment = paths[0].toLowerCase();
    const contestSlug = paths[1];
    const contestLabel =
      routeLabelMap[contestSegment] ||
      (contestSegment === "contests" ? "Contests" : "Contest");

    return [
      {
        title: "Home",
        href: "/",
      },
      {
        title: contestLabel,
        href: `/${city}/contests`,
      },
      {
        title: currentPageTitle || formatSlug(contestSlug),
      },
    ];
  }

  // Artist detail without city: Home > Artists > artist_name
  if (
    paths.length >= 2 &&
    ["artist", "artists"].includes(paths[0].toLowerCase())
  ) {
    return [
      {
        title: "Home",
        href: "/",
      },
      {
        title: "Artists",
        href: "/artists",
      },
      {
        title: currentPageTitle || formatSlug(paths[1]),
      },
    ];
  }

  // City-prefixed Artist detail: Home > City > Artists > artist_name
  if (
    paths.length >= 3 &&
    ["artist", "artists"].includes(paths[1].toLowerCase())
  ) {
    const routeCity = paths[0];
    return [
      {
        title: "Home",
        href: "/",
      },
      {
        title: formatSlug(routeCity),
        href: `/${routeCity}/events`,
      },
      {
        title: "Artists",
        href: "/artists",
      },
      {
        title: currentPageTitle || formatSlug(paths[2]),
      },
    ];
  }

  // Venue detail: Home > venue_name (skip the "venue" segment)
  if (
    paths.length >= 2 &&
    (paths[0].toLowerCase() === "venue" || paths[0].toLowerCase() === "venues")
  ) {
    return [
      {
        title: "Home",
        href: "/",
      },
      {
        title: currentPageTitle || formatSlug(paths[1]),
      },
    ];
  }

  // City-prefixed Venue detail: Home > venue_name
  if (
    paths.length >= 3 &&
    (paths[1].toLowerCase() === "venue" || paths[1].toLowerCase() === "venues")
  ) {
    return [
      {
        title: "Home",
        href: "/",
      },
      {
        title: currentPageTitle || formatSlug(paths[2]),
      },
    ];
  }

  const isContestPath = paths.some((p) =>
    ["contest", "contests"].includes(p.toLowerCase()),
  );

  const items = [
    {
      title: "Home",
      href: "/",
    },
  ];

  let currentPath = "";

  paths.forEach((segment, index) => {
    currentPath += `/${segment}`;

    if (
      index === 0 &&
      ["explore", "organizer"].includes(segment.toLowerCase())
    ) {
      return;
    }

    // Determine custom or mapped label
    let label =
      customLabels[segment] || customLabels[decodeURIComponent(segment)];

    if (!label) {
      if (routeLabelMap[segment.toLowerCase()]) {
        label = routeLabelMap[segment.toLowerCase()];
      } else {
        label = formatSlug(segment);
      }
    }

    // Special category page mapping
    const isCategoryChild =
      index > 0 && paths[index - 1].toLowerCase() === "category";

    if (
      isCategoryChild &&
      !label.toLowerCase().endsWith("shows") &&
      !label.toLowerCase().endsWith("events") &&
      !label.toLowerCase().endsWith("expos")
    ) {
      label = `${label} Shows`;
    }

    // City breadcrumb: contests pages → /city/contests, otherwise /city/events
    let href = currentPath;
    if (index === 0) {
      const segLower = segment.toLowerCase();
      if (!routeLabelMap[segLower] && segLower !== "explore") {
        href = isContestPath ? `/${segment}/contests` : `/${segment}/events`;
      } else if (segLower === "events" || segLower === "event") {
        href = `/${city}/events`;
      } else if (segLower === "contests" || segLower === "contest") {
        href = `/${city}/contests`;
      }
    }

    items.push({
      title: label,
      href,
    });
  });

  // Override last breadcrumb title if provided
  if (currentPageTitle && items.length > 0) {
    items[items.length - 1] = {
      ...items[items.length - 1],
      title: currentPageTitle,
    };
  }

  return items;
};
export const generateBreadcrumbSchema = (
  breadcrumbs,
  baseURL = "https://tykkit.com",
) => {
  const cleanBase = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.title,
      item: item.href
        ? `${cleanBase}${item.href === "/" ? "" : item.href}`
        : undefined,
    })),
  };
};
